/// REDUX ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
/// REACT ///
import { useEffect } from "react";
import { useState } from "react";
/// ROUTER ///
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
/// TYPES ///
import { Quote } from "api/types/quote";
/// ACTIONS ///
import { updateQuote } from "api/actions/quotes/quote";
/// MUI ///
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
/// COMPONENTS ///
import BaseButton from "components/universal/button/base";
import StoreColorOptionsTable from "components/store_quote/quote_preview/color_options";
import Loading from "components/universal/loading";
import SetStoreDiscount from "./set_discount";
import StoreQuoteInfo from "./info";
import StoreQuoteSummary from "./summary";
/// ICONS ///
import { faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { faFileInvoiceDollar } from "@fortawesome/free-solid-svg-icons";
import { faPercent } from "@fortawesome/free-solid-svg-icons";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
/// STYLES ///
import { item_style } from "styles/quote_preview/section";
import { header_style } from "styles/universal/section_header";
/// URLS ///
import { detail_url } from "components/navigation/endpoints";
import { market_url } from "components/navigation/endpoints";
import { print_url } from "components/navigation/endpoints";
import { quote_url } from "components/navigation/endpoints";
import { store_url } from "components/navigation/endpoints";
/// FUNCTIONS ///
import { confirmCutShapesFitted } from "functions/sketch/check/fit";
import { checkQuoteLineItemErrors } from "functions/sketch/check/quote";

interface Props {
  index: number,
  quote: Quote
}

export default function StoreQuoteDetails({ index, quote }: Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { areas } = useAppSelector((state) => state.quote);
  const { updating } = useAppSelector((state) => state.quote);
  const { cut_shapes } = useAppSelector((state) => state.sketch);
  const [error, setError] = useState<boolean>(false);
  const [fitted, setFitted] = useState<boolean>(true);
  const [open_discount, setOpenDiscount] = useState<boolean>(false);

  useEffect(() => {
    if (index === 6) {
      let is_fitted: boolean = false;
      try {
        is_fitted = confirmCutShapesFitted(cut_shapes, areas);
      }
      catch {
        is_fitted = false;
      }
      const has_errors: boolean = checkQuoteLineItemErrors(areas);
      setFitted(is_fitted);
      setError(has_errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, areas]);

  const generateQuote = () => {
    const updated_quote: Quote = {
      generated: true
    }
    if (quote.id) {
      dispatch(updateQuote(updated_quote, quote.id, true, false));
    }
  }

  return (
    <Grid container alignItems="center" sx={{ paddingTop: "20px", paddingBottom: "80px", paddingLeft: "25px", paddingRight: "25px" }}>
      <Grid item xs={2}>
        <Typography variant="h6" sx={item_style(false)}>
          Standard Quote
        </Typography>
      </Grid>
      <Grid item xs={10} textAlign="right">
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <BaseButton text="Print" icon={faPrint} clickEvent={() => window.open(`/${store_url}/${quote_url}/${detail_url}/${id}/${print_url}/`, "_blank")} />
          <BaseButton text="Get Quote" icon={faFileInvoiceDollar} clickEvent={generateQuote} disabled={!fitted || !quote.changed || !quote?.customer_data || !quote?.job_data} />
          <BaseButton text="Discount" icon={faPercent} clickEvent={() => setOpenDiscount(true)} />
          <BaseButton text="Market" icon={faDollarSign} clickEvent={() => navigate(`/${market_url}/${quote.id}`)} disabled={!quote.customer || !quote.generated || quote.changed || error} />
          <SetStoreDiscount open={open_discount} setOpen={setOpenDiscount} quote={quote} />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Box sx={header_style}></Box>
      </Grid>
      <StoreQuoteInfo quote={quote} />
      <StoreColorOptionsTable areas={areas} />
      <StoreQuoteSummary areas={areas} quote={quote} is_fitted={fitted} generateQuote={generateQuote} />
      <Loading loading={updating} />
    </Grid>
  )
}
